import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Button,
  InputGroup,
  InputGroupAddon,
  Input,
  InputGroupText,
  Badge
} from 'reactstrap';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ModalConfirmation from 'components/Modal/ConfirmationModal';
import { useHistory } from 'react-router-dom';
import ReactLoader from 'components/Loader';
import useMonitoringQueueGroups from 'hooks/useMonitoringQueueGroups';
import { FlexTableV2 } from 'components/FlexTableV2';

const MonitoringQueueGroupList = () => {
  const history = useHistory();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});

  const [idResource, setIdResource] = useState();
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [searchText, setSearchText] = useState('');

  const {
    response: groups,
    totalRegisters,
    isLoading,
    fetch,
    deleteGroup
  } = useMonitoringQueueGroups({ itemsPerPage: pagination.pageSize });

  useEffect(() => {
    fetch(pagination.pageIndex + 1, searchText);
  }, [pagination.pageIndex, pagination.pageSize, searchText, fetch]);

  const onSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleUpdate = (id) => {
    history.push(`/admin/chat/monitoring-queue-group/form/${id}`);
  };

  const controlDeleteResource = (id) => {
    setIdResource(id);
    setOpenModalConfirmation(true);
  };

  const confirmDelete = async (id) => {
    const success = await deleteGroup(id);
    if (success) {
      setOpenModalConfirmation(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Nome do Agrupamento',
        accessorKey: 'name',
        size: 200
      },
      {
        header: 'Filas',
        accessorKey: 'queues',
        size: 400,
        cell: ({ row }) => {
          const queues = row.original.queues || [];
          return (
            <div className="d-flex flex-wrap">
              {queues.map((queue) => (
                <Badge
                  key={queue.id}
                  color="info"
                  pill
                  className="mr-1 mb-1"
                  style={{ fontSize: '0.75rem' }}
                >
                  {queue.name}
                </Badge>
              ))}
            </div>
          );
        }
      },
      {
        header: 'Criado por',
        accessorKey: 'createdBy',
        size: 150
      },
      {
        id: 'actions',
        header: 'Ações',
        size: 100,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              color="info"
              onClick={() => handleUpdate(row.original.id)}
              className="mr-2 my-auto"
              title="Editar"
            >
              <li className="fas fa-edit"></li>
            </Button>
            <Button
              size="sm"
              color="danger"
              onClick={() => controlDeleteResource(row.original.id)}
              className="my-auto"
              title="Excluir"
            >
              <li className="fas fa-trash"></li>
            </Button>
          </div>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <SimpleHeader
        setCreate="/admin/chat/monitoring-queue-group/form"
        name="Agrupamento de Filas"
        parentName="Monitoramento"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Agrupamentos Cadastrados</h3>
                </CardHeader>
                <CardBody>
                  <Row className="mb-4">
                    <div className="col-md-12">
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fas fa-search" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Buscar por nome..."
                          type="text"
                          value={searchText}
                          onChange={onSearch}
                        />
                      </InputGroup>
                    </div>
                  </Row>

                  {isLoading ? (
                    <Row className="justify-content-md-center py-4">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <FlexTableV2
                      state={{
                        pagination,
                        setPagination,
                        columnOrder,
                        setColumnOrder,
                        columnVisibility,
                        setColumnVisibility,
                        columnSizing,
                        setColumnSizing
                      }}
                      table={{
                        data: groups,
                        columns,
                        rowCount: totalRegisters,
                        manualPagination: true,
                        enableLocalStorageMemo: true,
                        enableLocalStorageClearing: true,
                        scrollContainerHeight: 'unset'
                      }}
                    />
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>

        <ModalConfirmation
          isModalOpen={openModalConfirmation}
          onModalToggle={setOpenModalConfirmation}
          unmountOnClose={true}
          idResource={idResource}
          deleteResource={confirmDelete}
          message={'Tem certeza que deseja excluir este Agrupamento de Filas?'}
        />
      </Container>
    </>
  );
};

export default MonitoringQueueGroupList;
